/** Team (admin): invite people and bots with a role (one-time join link, shown once), see who is on the team and
 * online, revoke pending invites and agents (an agent's revoke is the token revoke). */
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { ErrorBox } from "../../components/ErrorBox";
import { useAction } from "../lib/actions";
import { fmtWhen } from "../lib/time";
import { ActionError, ConfirmButton, CopyButton, Empty, QueryView, Section, TableScroll } from "../lib/ui";

type Role = Schemas["RoleOut"];
type InviteKind = "human" | "bot";
const NAME_RE = /^[a-z0-9][a-z0-9._-]{1,31}$/;
const AGENTS_KEY = qk.list("agents");
const kindLabel = (k: string) => (k === "agent" ? "bot" : k);

function CreatedInvite({ inv, roleTitle, onDone }: { inv: Schemas["InviteCreated"]; roleTitle: string; onDone: () => void }) {
  return (
    <div className="ws7-secret" role="region" aria-label="New join link (shown once)" data-testid="team-invite-created">
      <p>
        <strong>Join link for {inv.name} ({roleTitle}, {kindLabel(inv.kind)})</strong>: shown once, single use, expires{" "}
        {fmtWhen(inv.expires_at)}. Send it to that person or bot only.
      </p>
      <p>Link: <code data-testid="team-invite-url">{inv.join_url}</code></p>
      <div className="ws7-row">
        <CopyButton text={inv.join_url} label="Copy join link" testId="team-invite-copy" what={`join link for ${inv.name}`} />
        <button type="button" data-testid="team-invite-done" onClick={onDone}>Done (hide link)</button>
      </div>
    </div>
  );
}

export function TeamTab() {
  const roles = useQuery({ queryKey: qk.list("roles"), queryFn: () => unwrap(api.GET("/api/agent/roles")), staleTime: 300_000 });
  const team = useQuery({ queryKey: AGENTS_KEY, queryFn: () => unwrap(api.GET("/api/agents")), refetchInterval: 30_000 });
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [kind, setKind] = useState<InviteKind | null>(null);
  const [ttlText, setTtlText] = useState("24");
  const ttl = /^\d{1,3}$/.test(ttlText.trim()) ? Number(ttlText.trim()) : NaN;
  const ttlOk = ttl >= 1 && ttl <= 168;
  const [created, setCreated] = useState<{ inv: Schemas["InviteCreated"]; title: string } | null>(null);
  const roleList = roles.data ?? [];
  const role: Role | undefined = roleList.find((r) => r.id === roleId) ?? roleList[0];
  const effKind: InviteKind = kind ?? (role?.kind_default === "human" ? "human" : "bot");
  const nameOk = NAME_RE.test(name.trim());

  const create = useAction(
    (body: Schemas["InviteIn"]) => unwrap(api.POST("/api/agents/invites", { body })),
    {
      label: (b) => `Invite ${b.name}`,
      success: (inv) => `Created a join link for ${inv.name}; copy it now, it is shown once`,
      invalidate: [AGENTS_KEY],
    },
  );
  const revokeInvite = useAction(
    (i: Schemas["InviteOut"]) => unwrap(api.DELETE("/api/agents/invites/{invite_id}", { params: { path: { invite_id: i.id } } })),
    { label: (i) => `Revoke invite #${i.id}`, success: (_r, i) => `Revoked the invite for ${i.name}`, invalidate: [AGENTS_KEY] },
  );
  const revokeAgent = useAction(
    (a: Schemas["AgentOut"]) => unwrap(api.DELETE("/api/tokens/{token_id}", { params: { path: { token_id: a.token_id } } })),
    {
      label: (a) => `Revoke ${a.actor}`,
      success: (_r, a) => `Revoked ${a.actor} (token #${a.token_id}); their sessions ended`,
      invalidate: [AGENTS_KEY, qk.list("tokens")],
    },
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!role || !nameOk || !ttlOk) return;
    const inv = await create.run({ name: name.trim(), role: role.id, kind: effKind, ttl_h: ttl });
    if (inv) {
      setCreated({ inv, title: role.title });
      setName("");
    }
  };

  return (
    <div className="ws7-page">
      <Section id="team-invite" title="Invite a person or bot">
        {created && <CreatedInvite inv={created.inv} roleTitle={created.title} onDone={() => setCreated(null)} />}
        <QueryView q={roles} what="roles">
          {() => (
            <form className="ws7-form" aria-label="Create a join link" onSubmit={submit}>
              <div className="ws7-toolbar">
                <label className="ws7-field">
                  Name
                  <input value={name} required maxLength={32} data-testid="team-name" placeholder="e.g. anna or grok-qa"
                    aria-invalid={name !== "" && !nameOk} aria-describedby="team-name-help" onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="ws7-field">
                  Role
                  <select value={role?.id ?? ""} data-testid="team-role" onChange={(e) => { setRoleId(e.target.value); setKind(null); }}>
                    {roleList.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                  </select>
                </label>
                <label className="ws7-field">
                  Kind
                  <select value={effKind} data-testid="team-kind" onChange={(e) => setKind(e.target.value as InviteKind)}>
                    <option value="human">human (browser session)</option>
                    <option value="bot">bot (API token)</option>
                  </select>
                </label>
                <label className="ws7-field">
                  Link valid (hours)
                  <input type="number" min={1} max={168} step={1} value={ttlText} data-testid="team-ttl"
                    aria-invalid={!ttlOk} onChange={(e) => setTtlText(e.target.value)} />
                </label>
              </div>
              <p id="team-name-help" className="ws7-muted">
                Lowercase letters, digits, . _ - (2–32). One name per person or bot; the same name with the same role
                and kind can join again from another device.
              </p>
              {role && (
                <p className="ws7-muted" data-testid="team-role-summary">
                  <strong>{role.title}</strong>: {role.summary} Scopes: {role.scopes.join(", ")}. Start page: {role.home}
                  {role.queue ? `, work queue: ${role.queue}` : ""}. <Link to={`/agent/${role.id}`}>Brief</Link>
                </p>
              )}
              <div className="ws7-row">
                <button type="submit" className="ws7-primary" data-testid="team-create" disabled={create.pending || !nameOk || !ttlOk || !role}>
                  Create join link
                </button>
              </div>
              <ErrorBox error={create.error} />
            </form>
          )}
        </QueryView>
      </Section>
      <Section id="team-list" title="Team">
        <ActionError error={revokeAgent.error ?? revokeInvite.error} onReload={() => void team.refetch()} />
        <QueryView q={team} what="the team">
          {(t) => (
            <>
              <h3>Pending invites</h3>
              {t.invites.length === 0 ? <Empty>No pending invites.</Empty> : (
                <TableScroll label="Pending invite table">
                  <table className="ws7-table" aria-label="Pending invites" data-testid="invites-table">
                    <thead>
                      <tr><th scope="col">Name</th><th scope="col">Role</th><th scope="col">Kind</th><th scope="col">By</th>
                        <th scope="col">Expires</th><th scope="col">Actions</th></tr>
                    </thead>
                    <tbody>
                      {t.invites.map((i) => (
                        <tr key={i.id} data-testid={`invite-row-${i.id}`}>
                          <td>{i.name}</td><td>{i.role_title ?? i.role}</td><td>{kindLabel(i.kind)}</td><td>{i.created_by}</td>
                          <td>{fmtWhen(i.expires_at)}</td>
                          <td>
                            <ConfirmButton label={`Revoke invite for ${i.name}`} confirmLabel={`Confirm revoke invite #${i.id}`}
                              testId={`invite-revoke-${i.id}`} disabled={revokeInvite.pending} onConfirm={() => void revokeInvite.run(i)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
              <h3>People and bots</h3>
              {t.agents.length === 0 ? <Empty>No one yet.</Empty> : (
                <TableScroll label="Team table">
                  <table className="ws7-table" aria-label="Team" data-testid="agents-table">
                    <thead>
                      <tr><th scope="col">Online</th><th scope="col">Name</th><th scope="col">Role</th><th scope="col">Kind</th>
                        <th scope="col">Scopes</th><th scope="col">Last used</th><th scope="col">Actions</th></tr>
                    </thead>
                    <tbody>
                      {t.agents.map((a) => (
                        <tr key={a.token_id} data-testid={`agent-row-${a.token_id}`}>
                          <td data-testid={`agent-online-${a.token_id}`}>
                            <span className={`ws7-dot ${a.online ? "on" : "off"}`} aria-hidden="true">●</span>{" "}
                            {a.online ? `online${a.page ? ` (${a.page})` : ""}` : "offline"}
                          </td>
                          <td>{a.actor}</td>
                          <td>{a.role_title ?? a.role ?? "—"}</td>
                          <td>{kindLabel(a.kind)}</td>
                          <td>{a.scopes.join(", ")}</td>
                          <td>{fmtWhen(a.last_used_at) || "never"}</td>
                          <td>
                            <ConfirmButton label={`Revoke ${a.actor}`} confirmLabel={`Confirm revoke ${a.actor} (token #${a.token_id})`}
                              testId={`agent-revoke-${a.token_id}`} disabled={revokeAgent.pending} onConfirm={() => void revokeAgent.run(a)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </>
          )}
        </QueryView>
      </Section>
    </div>
  );
}
